import {
  getDigitalRamNameHistoryApi,
  getLastNDays_DigitalRamNameCountApi,
} from "../../RamNamBookCRUD";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Card,
  Spinner,
  Badge,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Chart, registerables } from "chart.js";
Chart.register(...registerables);

/* ─────────────────────────────────────────────
   Weekly Bar Chart — Chart.js
───────────────────────────────────────────── */
const WeeklyBarChart = ({ data }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const ctx = canvasRef.current.getContext("2d");

    // ── Custom plugin: draw count value directly above each bar ──
    const countOnBarPlugin = {
      id: "countOnBar",
      afterDatasetsDraw(chart) {
        const {
          ctx: c,
          data: cd,
          scales: { x, y },
        } = chart;
        c.save();
        c.font = "bold 14px sans-serif";
        c.textAlign = "center";
        c.textBaseline = "bottom";
        cd.datasets[0].data.forEach((val, i) => {
          if (!val || val === 0) return;
          const xPos = x.getPixelForValue(i);
          const yPos = y.getPixelForValue(val) - 3;
          c.fillStyle = i === data.length - 1 ? "#FF4F00" : "#555";
          c.fillText(val.toLocaleString(), xPos, yPos);
        });
        c.restore();
      },
    };

    chartRef.current = new Chart(ctx, {
      type: "bar",
      plugins: [countOnBarPlugin],
      data: {
        // Two-line label: day abbreviation + date
        labels: data.map((d) => [d.dayLabel, d.dayName.slice(0, 3)]),
        datasets: [
          {
            label: "Ram Naam",
            data: data.map((d) => d.totalCount),
            backgroundColor: data.map((d, i) =>
              i === data.length - 1 ? "#FF4F00" : "#9db0e7",
            ),
            borderColor: data.map((d, i) =>
              i === data.length - 1 ? "#d62a17" : "#6f90e2",
            ),
            borderWidth: 1,
            barPercentage: 0.6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 20 }, // room for the count label above bars
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => data[items[0].dataIndex].dayLabel,
              label: (item) => ` ${item.raw.toLocaleString()} Ram Naam`,
            },
          },
        },
        scales: {
          x: {
            title: {
              display: true,
              text: "Day",
              font: { size: 12, weight: "700" },
              color: "#000",
            },
            ticks: {
              font: { size: 14, weight: "600" },
              autoSkip: false,
              color: (ctx) =>
                ctx.index === data.length - 1 ? "#FF4F00" : "#000",
            },
            grid: { display: false },
          },
          y: {
            title: {
              display: true,
              text: "Ram Naam Count",
              font: { size: 14, weight: "700" },
              color: "#000",
            },
            beginAtZero: true,
            ticks: {
              font: { size: 14, weight: "600" },
              precision: 0,
            },
            grid: { color: "#e7e3e3" },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, [data]);

  return (
    <div style={{ padding: "14px 14px 10px" }}>
      {/* Increased height to 260px to fit 2-line x-axis labels */}
      <div style={{ position: "relative", height: "260px" }}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label="Weekly Ram Naam chanting activity bar chart"
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main Page
───────────────────────────────────────────── */
const DigitalHistoryPage = () => {
  const navigate = useNavigate();

  const [historyData, setHistoryData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState(false);

  const memberId = JSON.parse(
    localStorage.getItem("customer"),
  )?.ramNamBookMemberID;

  const loadHistory = useCallback(async () => {
    try {
      const res = await getDigitalRamNameHistoryApi(memberId);
      if (res.data.isSuccess) setHistoryData(res.data.data.historyList || []);
    } catch (err) {
      toast.error(err.message || "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  const loadChart = useCallback(async () => {
    try {
      setChartLoading(true);
      setChartError(false);
      const res = await getLastNDays_DigitalRamNameCountApi(memberId);
      if (res.data.isSuccess) setChartData(res.data.data || []);
      else setChartError(true);
    } catch {
      setChartError(true);
    } finally {
      setChartLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    loadHistory();
    loadChart();
  }, [loadHistory, loadChart]);

  const totalWeek = chartData.reduce((s, d) => s + d.totalCount, 0);
  const activeDays = chartData.filter((d) => d.totalCount > 0).length;
  const peakDay = chartData.reduce(
    (a, b) => (b.totalCount > a.totalCount ? b : a),
    { totalCount: 0, dayName: "—" },
  );

  return (
    <>
      <style>{`
        /* ── Animations ── */
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position:  400px 0; }
        }

        /* ══════════════════════════════
           PAGE HEADER
        ══════════════════════════════ */
        .dh-header {
          background: linear-gradient(135deg, #FF4F00 0%, #e84118 100%);
          padding: 14px 0 18px;
          position: relative;
        }
        .dh-title {
          color: #fff;
          font-weight: 800;
          font-size: clamp(15px, 3vw, 20px);
          line-height: 1.2;
          margin: 0;
        }
        .dh-subtitle {
          color: rgba(255, 255, 255, 0.93);
          font-size: clamp(11px, 2vw, 13px);
          margin: 2px 0 0;
        }
        .dh-back-btn {
          background: rgba(255,255,255,0.16);
          border: 1px solid rgba(255,255,255,0.28);
          color: #fff;
          border-radius: 50px;
          padding: 5px 14px;
          font-size: clamp(14px, 4vw, 15px);
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.2s;
        }
        .dh-back-btn:hover { background: rgba(255,255,255,0.26); }

        /* ══════════════════════════════
           SUMMARY CHIPS
        ══════════════════════════════ */
        .stat-chip {
          background: #fff;
          border: 1px solid #f5e8e8;
          border-radius: 14px;
          padding: 10px 8px;
          text-align: center;
          box-shadow: 0 1px 6px rgba(192,57,43,0.08);
        }
        .stat-val {
          font-size: clamp(16px, 4vw, 22px);
          font-weight: 800;
          color: #FF4F00;
          line-height: 1;
        }
        .stat-lbl {
          font-size: clamp(9px, 1.9vw, 11px);
          font-weight: 700;
          color: #252222;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          margin-top: 3px;
        }

        /* ══════════════════════════════
           CARD COMMON
        ══════════════════════════════ */
        .dh-card {
          border-radius: 16px !important;
          border: 0 !important;
          box-shadow: 0 2px 14px rgba(0,0,0,0.07) !important;
          overflow: hidden;
        }
        .dh-card-header {
          background: #fff !important;
          border-bottom: 1px solid #f2eeeb !important;
          padding: 10px 14px !important;
        }
        .dh-card-title {
          font-size: clamp(12px, 2vw, 14px);
          font-weight: 700;
          color: #333;
        }

        /* ══════════════════════════════
           TABLE
        ══════════════════════════════ */
        .dh-table { font-size: clamp(12px, 2vw, 14px); }
        .history-scroll {
          overflow-y: auto;
          max-height: clamp(240px, calc(100vh - 420px), 70vh);
        }
        @media (min-width: 992px) {
          .history-scroll {
            max-height: clamp(300px, calc(100vh - 180px), 82vh);
          }
        }

        .col-serial { display: none; }
        @media (min-width: 480px) { .col-serial { display: table-cell; } }

        .dh-table thead th {
          font-size: clamp(11px, 1.8vw, 13px);
          font-weight: 700;
          padding: 10px 8px;
          white-space: nowrap;
        }
        .dh-table tbody td { padding: 9px 8px; }

        /* ══════════════════════════════
           SKELETON / EMPTY STATES
        ══════════════════════════════ */
        .chart-skeleton {
          margin: 14px;
          height: clamp(80px, 14vh, 150px);
          border-radius: 8px;
          background: linear-gradient(90deg, #f5eeea 25%, #ede6e2 50%, #f5eeea 75%);
          background-size: 400px 100%;
          animation: shimmer 1.4s ease-in-out infinite;
        }
        .dh-empty {
          text-align: center;
          padding: 36px 20px;
          color: #bbb;
          font-size: 13px;
        }

        /* ══════════════════════════════
           DESKTOP LAYOUT
        ══════════════════════════════ */
        @media (min-width: 992px) {
          .dh-columns { align-items: stretch; }
          .dh-col-chart { display: flex; flex-direction: column; }
          .dh-chart-card { flex: 1; }
        }
      `}</style>

      {/* ══════════════════════════════
          PAGE HEADER
      ══════════════════════════════ */}
      <div className="dh-header">
        <Container fluid="xl">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <div>
              <p className="dh-title">Digital Ram Name</p>
              <p className="dh-subtitle">Chanting History</p>
            </div>
            <button
              className="dh-back-btn"
              onClick={() => navigate("/member/dashboard")}
            >
              ← Back
            </button>
          </div>
        </Container>
      </div>

      {/* ══════════════════════════════
          MAIN CONTENT
      ══════════════════════════════ */}
      <Container fluid="xl" className="py-3 py-md-4 px-2 px-sm-3 px-lg-4">
        {/* ── Summary chips ── */}
        {/* {!chartLoading && chartData.length > 0 && (
          <Row className="g-2 mb-3">
            <Col xs={4}>
              <div className="stat-chip">
                <div className="stat-val">{totalWeek.toLocaleString()}</div>
                <div className="stat-lbl">This Week</div>
              </div>
            </Col>
            <Col xs={4}>
              <div className="stat-chip">
                <div className="stat-val">{activeDays}</div>
                <div className="stat-lbl">Active Days</div>
              </div>
            </Col>
            <Col xs={4}>
              <div className="stat-chip">
                <div className="stat-val">
                  {peakDay.totalCount > 0
                    ? peakDay.totalCount.toLocaleString()
                    : "—"}
                </div>
                <div className="stat-lbl">Best Day</div>
              </div>
            </Col>
          </Row>
        )} */}

        <Row className="g-3 dh-columns">
          {/* ── Chart card ── */}
          <Col xs={12} className="dh-col-chart">
            <Card className="dh-card dh-chart-card">
              <Card.Header className="dh-card-header d-flex justify-content-between align-items-center">
                <span className="dh-card-title fs-5">📊 Last 5 days Chart</span>
              </Card.Header>
              <Card.Body className="p-0">
                {chartLoading ? (
                  <div className="chart-skeleton" />
                ) : chartError ? (
                  <div className="dh-empty">
                    Could not load chart.{" "}
                    <span
                      className="text-danger"
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={loadChart}
                    >
                      Retry
                    </span>
                  </div>
                ) : chartData.length > 0 ? (
                  <WeeklyBarChart data={chartData} />
                ) : (
                  <div className="dh-empty">No chart data</div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* ── History table card ── */}
          <Col xs={12}>
            <Card className="dh-card">
              <Card.Header className="dh-card-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <h5
                    className="mb-0 fw-bold text-[#FF4F00]"
                    style={{ fontSize: "clamp(15px, 2.5vw, 18px)" }}
                  >
                    Digital Ram Name History
                  </h5>
                  {historyData.length > 0 && (
                    <Badge
                      bg="danger"
                      className="bg-opacity-10 text-danger fw-semibold d-none d-sm-inline"
                      style={{ fontSize: 12 }}
                    >
                      {historyData.length} records
                    </Badge>
                  )}
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {loading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center py-4 gap-2">
                    <Spinner animation="border" variant="danger" />
                    <span className="text-muted" style={{ fontSize: 13 }}>
                      Loading History...
                    </span>
                  </div>
                ) : historyData.length > 0 ? (
                  <div className="history-scroll">
                    <Table
                      hover
                      className="mb-0 align-middle text-center dh-table"
                    >
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          background: "#9c7171",
                          zIndex: 10,
                          boxShadow: "0 2px 5px rgba(0,0,0,0.06)",
                        }}
                      >
                        <tr className="bg-light">
                          <th className="col-serial fs-6">#</th>
                          <th className="fs-6">Ram Naam</th>
                          <th className="fs-6">Device</th>
                          <th className="fs-6">Date & Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.map((item, index) => (
                          <tr
                            key={item.digitalRAMNameID}
                            style={{ cursor: "pointer" }}
                          >
                            <td className="col-serial text-muted">
                              {index + 1}
                            </td>
                            <td className="fw-bold text-success">
                              {item.noOfDigitalRAMName}
                            </td>
                            <td>
                              <span className="badge bg-light text-dark border">
                                {item.deviceTypeName}
                              </span>
                            </td>
                            <td className="text-muted">{item.createDate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="dh-empty">
                    <div style={{ fontSize: 30, marginBottom: 8 }}>🙏</div>
                    <h6 className="text-muted">No History Found</h6>
                    <small className="text-muted">
                      Your chanting history will appear here
                    </small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DigitalHistoryPage;
